import React, { useRef } from "react";
import { Accordion, Button, ButtonGroup, Card, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppApi } from "@/App";
import VkVideoStatus from "@components/Common/VKVideo/VKVideoStatus";
import VkVideoUploader from "@components/Common/VKVideo/VKVideoUploader";
import VkVideoPlayer from "@components/Common/VkVideoPlayer";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { DefaultLayout } from "@components/Layouts";
import { url_reverse } from "@components/Utils";

const RenderFormFields = ({ apiData, setApiData, prefix, submitBtnRef }) => {
    if (!apiData) return;

    return (
        <>
            {apiData.forms_data[prefix].ordered_fields &&
                apiData.forms_data[prefix].ordered_fields.map((name, i) => {
                    return (
                        <div key={`${prefix}.${apiData.forms_data[prefix].ordered_fields[i]}`}>
                            <DevForm.Field prefix={prefix} apiData={apiData} setApiData={setApiData} name={name} />
                            {name === "vkvideo_file" && (
                                <VKVideoSection
                                    setApiData={setApiData}
                                    apiData={apiData}
                                    btnRef={submitBtnRef}
                                    prefix={prefix}
                                />
                            )}
                        </div>
                    );
                })}
        </>
    );
};

const CollapsedFields = ({ fields }) => {
    const { t } = useTranslation();

    if (!fields || fields?.length === 0) {
        return <></>;
    }

    return (
        <Accordion className="mb-5">
            <Card>
                <Card.Header>
                    <Accordion.Toggle as={Button} variant="link" eventKey="0">
                        {t("Дополнительные поля")}
                    </Accordion.Toggle>
                </Card.Header>
                <Accordion.Collapse eventKey="0">
                    <Card.Body>
                        {fields.map((field, index) => (
                            <DevForm.Field key={`${field}_${index}`} prefix="rules" name={field} />
                        ))}
                    </Card.Body>
                </Accordion.Collapse>
            </Card>
        </Accordion>
    );
};

const LocaleEdit = ({ header, headerTools, apiData, setApiData, onFormSubmit, pocketUrl }) => {
    const { t } = useTranslation();
    const btnRef = useRef(null);

    if (!apiData) return;

    return (
        <Card>
            <Card.Header className="p-0">
                <div className="dmg-card-header-title">{header}</div>
                <div className="dmg-card-header-tools">{headerTools}</div>
            </Card.Header>
            <Card.Body>
                <Form onSubmit={onFormSubmit}>
                    <RenderFormFields apiData={apiData} setApiData={setApiData} prefix="data" submitBtnRef={btnRef} />
                    <ButtonGroup className="mt-3">
                        <DevForm.Button
                            variant="secondary"
                            name="save_and_close"
                            onClick={(ev) => onFormSubmit(ev, { backUrl: pocketUrl })}
                        >
                            {t("Сохранить")}
                        </DevForm.Button>
                        <DevForm.Button innerRef={btnRef} variant="primary" type="submit" name="save">
                            {t("Сохранить и продолжить")}
                        </DevForm.Button>
                    </ButtonGroup>
                </Form>
            </Card.Body>
        </Card>
    );
};

const VKVideoSection = ({ apiData, setApiData, btnRef, prefix }) => {
    const { t } = useTranslation();
    const removeVideoHandle = () => {
        const newData = { ...apiData };
        delete newData.forms_data[prefix].data.vkvideo_id;
        delete newData.forms_data[prefix].data.vkvideo_file;
        delete newData.vkvideo_data;
        setApiData(newData);
    };

    const RemoveButton = () => {
        return (
            <span
                className="fa fa-remove"
                style={{
                    fontSize: "0.7rem",
                    color: "red",
                    position: "absolute",
                    top: "0px",
                    right: "0px",
                    backgroundColor: "white",
                    cursor: "pointer",
                    padding: "1px",
                    border: "1px solid #dee2e6",
                    borderBottomLeftRadius: "9px",
                }}
                title={t("Удалить")}
                onClick={removeVideoHandle}
            />
        );
    };

    return (
        <>
            <Row>
                <Col sm="3" />
                <Col sm="9" className="my-auto">
                    <VkVideoStatus data={apiData?.vkvideo_data} />
                    {apiData?.vkvideo_data && (
                        <div style={{ position: "relative", width: "600px", height: "300px" }}>
                            <VkVideoPlayer data={apiData.vkvideo_data} height="300" />
                            <RemoveButton />
                        </div>
                    )}
                </Col>
            </Row>

            <VkVideoUploader
                apiData={apiData}
                setApiData={setApiData}
                prefix={prefix}
                btnRef={btnRef}
                urlForGetUploadLink={url_reverse("api:pockets:vkvideo_upload_link")}
            />
        </>
    );
};

const ItemEdit = ({ header, headerTools, apiData, setApiData, onFormSubmit, pocketUrl }) => {
    const { t } = useTranslation();
    const prefix = "data";
    const btnRef = useRef(null);
    const navigate = useNavigate();
    const { pocket } = apiData || {};

    if (!apiData) return;

    return (
        <Card>
            <Card.Header className="p-0">
                <div className="dmg-card-header-title">{header}</div>
                <div className="dmg-card-header-tools">{headerTools}</div>
            </Card.Header>
            <Card.Body>
                <Form
                    onSubmit={(ev) =>
                        onFormSubmit(ev, {
                            onOK: (resp) =>
                                navigate(url_reverse("admin:pockets:item", { slug: pocket?.slug, id: resp.id })),
                        })
                    }
                >
                    <RenderFormFields apiData={apiData} setApiData={setApiData} prefix={prefix} submitBtnRef={btnRef} />
                    <hr />
                    {Object.keys(apiData?.forms_data.rules.fields).map((key, index) => {
                        return (
                            !apiData?.collapsed_fields.includes(key) && (
                                <DevForm.Field key={`${key}_${index}_collapsed`} prefix="rules" name={key} />
                            )
                        );
                    })}
                    <CollapsedFields fields={apiData?.collapsed_fields} />
                    <ButtonGroup className="mt-3">
                        <DevForm.Button
                            variant="secondary"
                            name="save_and_close"
                            onClick={(ev) => onFormSubmit(ev, { backUrl: pocketUrl })}
                        >
                            {t("Сохранить")}
                        </DevForm.Button>
                        <DevForm.Button innerRef={btnRef} variant="primary" type="submit" name="save">
                            {t("Сохранить и продолжить")}
                        </DevForm.Button>
                    </ButtonGroup>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default function PocketsItemEditScreen() {
    const { t } = useTranslation();
    const { id, slug, locale } = useParams();
    // Для этих карманов не показываем выбор локали
    const pocketsWithHiddenLocales = ["play2-new", "play2-keys", "play2-discount", "play2-free-games"];
    const hideLocales = pocketsWithHiddenLocales.includes(slug);

    const api_url = locale
        ? url_reverse("api:pockets:item_locale", { id, slug, locale })
        : url_reverse("api:pockets:item", { id, slug });

    const { apiData, setApiData } = useAppApi(api_url);
    const { pocket, forms_data } = apiData || {};
    const onFormSubmit = useSubmit();
    const pocketUrl = url_reverse("admin:pockets:items", { slug: pocket?.slug });

    const header = (
        <>
            <Link to={`${url_reverse("admin:pockets:list")}#group_${pocket?.pocket_group_id}`}>{t("Карман")}</Link> –{" "}
            <Link to={pocketUrl}>{pocket?.name || ""}</Link>{" "}
            {forms_data?.data.data.name && `– ${forms_data?.data.data.name}`}
        </>
    );

    const headerTools = (
        <>
            {id !== "add" && (
                <ul className={`nav nav-tabs ${hideLocales ? "d-none" : ""}`}>
                    <li className="nav-item">
                        <Link
                            className={locale ? `nav-link` : `nav-link active`}
                            to={url_reverse("admin:pockets:item", { id, slug })}
                        >
                            RU
                        </Link>
                    </li>
                    {apiData?.locales &&
                        apiData?.locales.map((loc, index) => (
                            <li key={`${loc}_${index}`} className="nav-item">
                                <Link
                                    to={url_reverse("admin:pockets:item_locale", { id, slug, locale: loc })}
                                    className={locale === loc ? "nav-link active" : "nav-link"}
                                >
                                    {apiData?.existed_locales?.includes(loc) ? (
                                        <b>{loc.substring(0, 2).toUpperCase()}</b>
                                    ) : (
                                        loc.substring(0, 2).toUpperCase()
                                    )}
                                </Link>
                            </li>
                        ))}
                </ul>
            )}
        </>
    );

    return (
        <DefaultLayout>
            {locale ? (
                <LocaleEdit
                    header={header}
                    headerTools={headerTools}
                    apiData={apiData}
                    setApiData={setApiData}
                    onFormSubmit={onFormSubmit}
                    pocketUrl={pocketUrl}
                />
            ) : (
                <ItemEdit
                    header={header}
                    headerTools={headerTools}
                    apiData={apiData}
                    setApiData={setApiData}
                    onFormSubmit={onFormSubmit}
                    pocketUrl={pocketUrl}
                />
            )}
        </DefaultLayout>
    );
}
